import { useRef, useState, useEffect } from "react";
import EntityManager from "@/components/EntityManager";
import { api, API, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { printStaffDirectory, printStaffIdCard, printStaffIdCardsBulk, printStaffVisitingCard, printStaffVisitingCardsBulk, printBranchDirectory, printStaffJoiningLetter, printStaffExperienceLetter, printSalarySlip, printStaffBirthdayCard, printBirthdaysThisMonth, printNewJoinerKit, printNewJoinerKitsBulk, printBranchOpeningPack, branchOpeningPackSections, newJoinerKitHtml, VCARD_THEMES, staffIdCardShareHtml, staffVisitingCardShareHtml } from "@/lib/print";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, Upload, User, IdCard, Trash2, Contact, MessageCircle, Download, FileText, Send, FileCheck, Banknote, Wallet, UserPlus, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";

const exportDirectoryExcel = (rows) => {
  const active = (rows || []).filter((r) => (r.status || "Active") !== "Inactive");
  if (!active.length) return;
  const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const list = [...active].sort((a, b) => String(a.branch || "").localeCompare(String(b.branch || "")) || String(a.name || "").localeCompare(String(b.name || "")));
  const lines = ["Sl,Branch,Staff Code,Staff Name,Designation,Mobile No.,Email ID,Branch Address"];
  list.forEach((r, i) => lines.push([i + 1, r.branch || "Chennai", r.staff_code || "", r.name || "", r.designation || "", r.phone || "", r.email || "", r.branch_address || ""].map(cell).join(",")));
  const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `ntc-branch-directory-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};
import { toast } from "sonner";

import { BRANCHES } from "@/lib/branches";

function BulkIdCards({ rows, token, profile, cardTheme, setCardTheme, kitLang, setKitLang }) {
  const [branch, setBranch] = useState("All");
  const [waOpen, setWaOpen] = useState(false);
  const [sent, setSent] = useState({});
  const [busy, setBusy] = useState("");
  const [managers, setManagers] = useState({});
  const [contacts, setContacts] = useState({});
  const [mgrOpen, setMgrOpen] = useState(false);
  const [mgrSaving, setMgrSaving] = useState("");
  useEffect(() => {
    api.get("/branch-managers").then((r) => setManagers(r.data || {})).catch(() => {});
    api.get("/branch-contacts").then((r) => setContacts(r.data || {})).catch(() => {});
  }, []);
  const saveManager = async (b) => {
    setMgrSaving(b);
    try {
      const r = await api.put("/branch-managers", { branch: b, phone: managers[b] || "" });
      setManagers((m) => ({ ...m, [b]: r.data.manager_phone }));
      toast.success(`${b} manager mobile saved`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setMgrSaving("");
    }
  };
  const managerPhone = branch !== "All" ? managers[branch] || contacts[branch] || "" : "";
  const list = (rows || []).filter((r) => (r.status || "Active") !== "Inactive" && (branch === "All" || (r.branch || "Chennai") === branch));
  const sendOne = async (r) => {
    setBusy(r.id);
    try {
      await shareCardImageWhatsApp(r, "id", profile, token, cardTheme);
      setSent((s) => ({ ...s, [r.id]: true }));
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="flex items-center gap-2">
      <Select value={branch} onValueChange={setBranch}>
        <SelectTrigger className="h-9 w-[130px] text-sm" data-testid="bulk-idcard-branch"><SelectValue /></SelectTrigger>
        <SelectContent>{["All", ...BRANCHES].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={cardTheme} onValueChange={setCardTheme}>
        <SelectTrigger className="h-9 w-[130px] text-sm" data-testid="vcard-theme-select"><SelectValue /></SelectTrigger>
        <SelectContent>{Object.keys(VCARD_THEMES).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
      </Select>
      <Button variant="outline" disabled={!list.length} onClick={() => printStaffIdCardsBulk(list, token, branch)} data-testid="print-bulk-idcards-btn">
        <IdCard className="mr-2 h-4 w-4" /> Print ID Cards ({list.length})
      </Button>
      <Button variant="outline" disabled={!list.length} onClick={() => printStaffVisitingCardsBulk(list, profile, token, branch, cardTheme)} data-testid="print-bulk-vcards-btn">
        <Contact className="mr-2 h-4 w-4" /> Print Visiting Cards ({list.length})
      </Button>
      <Select value={kitLang} onValueChange={setKitLang}>
        <SelectTrigger className="h-9 w-[110px] text-sm" data-testid="kit-lang-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="English">English</SelectItem>
          <SelectItem value="Tamil">தமிழ்</SelectItem>
          <SelectItem value="Hindi">हिन्दी</SelectItem>
        </SelectContent>
      </Select>
      <Button variant="outline" className="border-teal-300 text-teal-700 hover:bg-teal-50" disabled={!list.length} onClick={() => printNewJoinerKitsBulk(list, branch, kitLang)} data-testid="print-bulk-joiner-kits-btn">
        <UserPlus className="mr-2 h-4 w-4" /> Joiner Kits ({list.length})
      </Button>
      <Button variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" disabled={!list.length} onClick={() => printBranchOpeningPack(list, token, branch, kitLang)} data-testid="print-branch-pack-btn">
        <Printer className="mr-2 h-4 w-4" /> Branch Pack ({list.length})
      </Button>
      <Button variant="outline" className="border-green-300 text-green-700 hover:bg-green-50" disabled={!list.length} title={managerPhone ? `Opens chat with ${branch} manager (${managerPhone})` : "No manager mobile saved — chat opens without a recipient"} onClick={() => shareBranchPackWhatsApp(list, token, branch, kitLang, managerPhone)} data-testid="wa-branch-pack-btn">
        <MessageCircle className="mr-2 h-4 w-4" /> WA Pack PDF
      </Button>
      <Button variant="outline" size="icon" className="h-9 w-9 border-slate-300 text-slate-600 hover:bg-slate-50" title="Branch manager mobile numbers — pack chats open straight to them" onClick={() => setMgrOpen(true)} data-testid="branch-managers-btn">
        <Phone className="h-4 w-4" />
      </Button>
      <Dialog open={mgrOpen} onOpenChange={setMgrOpen}>
        <DialogContent className="sm:max-w-[440px]" data-testid="branch-managers-dialog">
          <DialogHeader><DialogTitle className="font-display">Branch Manager Mobiles</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">The WA Pack PDF chat opens straight to this number for the selected branch. Falls back to the branch WhatsApp from Settings when empty.</p>
          <div className="space-y-2">
            {BRANCHES.map((b) => (
              <div key={b} className="flex items-center gap-2" data-testid="branch-manager-row">
                <span className="w-24 shrink-0 text-sm font-medium">{b}</span>
                <Input value={managers[b] || ""} placeholder={contacts[b] ? `fallback: ${contacts[b]}` : "10-digit mobile"} onChange={(e) => setManagers((m) => ({ ...m, [b]: e.target.value }))} data-testid={`manager-phone-input-${b.toLowerCase()}`} />
                <Button size="sm" variant="outline" disabled={mgrSaving === b} onClick={() => saveManager(b)} data-testid={`manager-phone-save-${b.toLowerCase()}`}>
                  {mgrSaving === b ? "…" : "Save"}
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" disabled={!list.length} onClick={() => { setSent({}); setWaOpen(true); }} data-testid="bulk-wa-idcards-btn">
        <MessageCircle className="mr-2 h-4 w-4" /> WA ID Cards ({list.length})
      </Button>
      <Dialog open={waOpen} onOpenChange={setWaOpen}>
        <DialogContent className="sm:max-w-[520px]" data-testid="bulk-wa-idcards-dialog">
          <DialogHeader><DialogTitle className="font-display">WhatsApp ID Cards — {branch === "All" ? "All Branches" : branch} ({list.length} staff)</DialogTitle></DialogHeader>
          <div className="max-h-[380px] space-y-1 overflow-y-auto rounded-md border p-2">
            {list.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 rounded px-2 py-1.5 hover:bg-slate-50" data-testid="bulk-wa-idcard-row">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{r.name}</div>
                  <div className="text-xs text-muted-foreground">{r.staff_code || "—"} · {r.branch || "Chennai"}{r.phone ? ` · ${r.phone}` : " · no mobile"}</div>
                </div>
                <Button size="sm" className={`h-8 shrink-0 ${sent[r.id] ? "bg-slate-200 text-slate-600 hover:bg-slate-200" : "bg-emerald-600 text-white hover:bg-emerald-700"}`} disabled={busy === r.id} onClick={() => sendOne(r)} data-testid="bulk-wa-idcard-send-btn">
                  <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> {busy === r.id ? "Preparing…" : sent[r.id] ? "Sent ✓" : "Send"}
                </Button>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Tap Send on each staff — the card image is shared (or downloaded) and their WhatsApp chat opens ready to attach.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const DOC_TYPES = [
  { key: "aadhaar", label: "Aadhaar Card" },
  { key: "joining_letter", label: "Joining Letter" },
  { key: "license", label: "Driving License" },
  { key: "pan", label: "PAN Card" },
  { key: "other", label: "Other Document" },
];

function DocumentsUploader({ form, setForm }) {
  const inputRef = useRef(null);
  const [uploadingType, setUploadingType] = useState("");
  const [pickType, setPickType] = useState("");
  const token = localStorage.getItem("ntc_token");
  if (!form.id) return null;
  const docs = form.documents || {};

  const pick = (type) => { setPickType(type); inputRef.current?.click(); };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !pickType) return;
    setUploadingType(pickType);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/staff-master/${form.id}/document/${pickType}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, documents: { ...(f.documents || {}), [pickType]: { path: res.data.path, filename: res.data.filename } } }));
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploadingType("");
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeDoc = async (type) => {
    if (!window.confirm("Remove this document?")) return;
    try {
      await api.delete(`/staff-master/${form.id}/document/${type}`);
      setForm((f) => { const d = { ...(f.documents || {}) }; delete d[type]; return { ...f, documents: d }; });
      toast.success("Document removed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="rounded-md border border-border bg-slate-50/60 px-3 py-2">
      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Documents (Aadhaar, License, etc.)</div>
      <div className="space-y-1.5">
        {DOC_TYPES.map(({ key, label }) => {
          const d = docs[key];
          return (
            <div key={key} className="flex items-center gap-2" data-testid={`doc-row-${key}`}>
              <span className="w-32 shrink-0 text-xs font-medium">{label}</span>
              {d?.path ? (
                <>
                  <a href={`${API}/files/${d.path}?auth=${token}`} target="_blank" rel="noreferrer" className="max-w-[140px] truncate text-xs font-medium text-blue-600 underline" data-testid={`doc-view-${key}`}>
                    {d.filename || "View"}
                  </a>
                  <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:text-destructive" onClick={() => removeDoc(key)} data-testid={`doc-remove-${key}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </>
              ) : (
                <Button type="button" variant="outline" size="sm" className="h-7 text-xs" disabled={!!uploadingType} onClick={() => pick(key)} data-testid={`doc-upload-${key}`}>
                  <Upload className="mr-1.5 h-3 w-3" /> {uploadingType === key ? "Uploading…" : "Upload"}
                </Button>
              )}
            </div>
          );
        })}
      </div>
      <p className="mt-1.5 text-[10px] text-muted-foreground">JPG / PNG / WEBP / PDF, max 10MB each</p>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={onPick} data-testid="doc-file-input" />
    </div>
  );
}

function PhotoUploader({ form, setForm }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dim, setDim] = useState(null);
  const dragRef = useRef(null);
  const token = localStorage.getItem("ntc_token");
  const V = 288;

  if (!form.id)
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="photo-hint">
        Save the staff first, then edit to upload a photo.
      </div>
    );

  const baseScale = (d) => Math.max(V / d.w, V / d.h);
  const clampPos = (p, z, d) => {
    const s = baseScale(d) * z;
    return { x: Math.min(0, Math.max(V - d.w * s, p.x)), y: Math.min(0, Math.max(V - d.h * s, p.y)) };
  };

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = "";
    if (!file) return;
    const rd = new FileReader();
    rd.onload = () => {
      setDim(null);
      setZoom(1);
      setPos({ x: 0, y: 0 });
      setCropSrc(rd.result);
    };
    rd.readAsDataURL(file);
  };

  const onImgLoad = (e) => {
    const d = { w: e.target.naturalWidth, h: e.target.naturalHeight };
    setDim(d);
    const s = baseScale(d);
    setPos({ x: (V - d.w * s) / 2, y: (V - d.h * s) / 2 });
  };

  const onZoom = (z) => {
    if (!dim) return;
    const sOld = baseScale(dim) * zoom;
    const sNew = baseScale(dim) * z;
    const cx = (V / 2 - pos.x) / sOld;
    const cy = (V / 2 - pos.y) / sOld;
    setZoom(z);
    setPos(clampPos({ x: V / 2 - cx * sNew, y: V / 2 - cy * sNew }, z, dim));
  };

  const onDown = (e) => {
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMove = (e) => {
    if (!dragRef.current || !dim) return;
    setPos(clampPos({ x: dragRef.current.ox + e.clientX - dragRef.current.sx, y: dragRef.current.oy + e.clientY - dragRef.current.sy }, zoom, dim));
  };
  const onUp = () => { dragRef.current = null; };

  const saveCrop = () => {
    if (!dim) return;
    setUploading(true);
    const img = new Image();
    img.onload = () => {
      const OUT = 480;
      const k = OUT / V;
      const s = baseScale(dim) * zoom;
      const canvas = document.createElement("canvas");
      canvas.width = OUT;
      canvas.height = OUT;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, OUT, OUT);
      ctx.drawImage(img, 0, 0, dim.w, dim.h, pos.x * k, pos.y * k, dim.w * s * k, dim.h * s * k);
      canvas.toBlob(async (blob) => {
        try {
          const fd = new FormData();
          fd.append("file", new File([blob], "photo.jpg", { type: "image/jpeg" }));
          const res = await api.post(`/staff-master/${form.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          setForm((f) => ({ ...f, photo_path: res.data.photo_path, photo_filename: res.data.filename }));
          toast.success("Photo cropped & uploaded");
          setCropSrc(null);
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail));
        } finally {
          setUploading(false);
        }
      }, "image/jpeg", 0.92);
    };
    img.src = cropSrc;
  };

  const s = dim ? baseScale(dim) * zoom : 1;
  return (
    <div className="flex items-center gap-3 rounded-md border border-border bg-slate-50/60 px-3 py-2">
      {form.photo_path ? (
        <img src={`${API}/files/${form.photo_path}?auth=${token}`} alt="Staff" className="h-14 w-14 rounded-md border border-border object-cover" data-testid="staff-photo-preview" />
      ) : (
        <span className="grid h-14 w-14 place-items-center rounded-md border border-dashed border-slate-300 text-slate-400"><User className="h-6 w-6" /></span>
      )}
      <div>
        <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => inputRef.current?.click()} data-testid="upload-photo-btn">
          <Upload className="mr-2 h-3.5 w-3.5" /> {uploading ? "Uploading…" : form.photo_path ? "Change Photo" : "Upload Photo"}
        </Button>
        <p className="mt-1 text-[11px] text-muted-foreground">JPG / PNG / WEBP — crop & zoom before saving</p>
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPick} data-testid="photo-file-input" />
      <Dialog open={!!cropSrc} onOpenChange={(o) => !o && !uploading && setCropSrc(null)}>
        <DialogContent className="sm:max-w-[400px]" data-testid="photo-crop-dialog">
          <DialogHeader><DialogTitle className="font-display">Crop Photo — drag to position, slide to zoom</DialogTitle></DialogHeader>
          <div
            className="relative mx-auto touch-none select-none overflow-hidden rounded-md border border-border bg-slate-100"
            style={{ width: V, height: V, cursor: "move" }}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            data-testid="photo-crop-viewport"
          >
            {cropSrc && (
              <img
                src={cropSrc}
                alt=""
                draggable={false}
                onLoad={onImgLoad}
                style={{ position: "absolute", left: pos.x, top: pos.y, width: dim ? dim.w * s : "auto", maxWidth: "none", pointerEvents: "none" }}
              />
            )}
            <div className="pointer-events-none absolute inset-0 rounded-md border-2 border-white/70" />
          </div>
          <div className="flex items-center gap-3 px-1">
            <span className="text-xs font-semibold text-muted-foreground">Zoom</span>
            <input type="range" min="1" max="3" step="0.01" value={zoom} onChange={(e) => onZoom(Number(e.target.value))} className="flex-1 accent-primary" data-testid="photo-zoom-slider" />
            <span className="w-10 text-right text-xs text-muted-foreground">{zoom.toFixed(1)}×</span>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={uploading} onClick={() => setCropSrc(null)}>Cancel</Button>
            <Button disabled={uploading || !dim} onClick={saveCrop} data-testid="photo-crop-save-btn">
              {uploading ? "Uploading…" : "Crop & Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const shareJoiningLetterWhatsApp = async (s) => {
  try {
    toast.info("Preparing joining letter PDF…");
    const res = await api.get(`/staff-master/${s.id}/letter.pdf`, { params: { kind: "joining" }, responseType: "blob" });
    const fname = `joining-letter-${String(s.staff_code || s.name || "staff").replace(/\s+/g, "-").toLowerCase()}.pdf`;
    const file = new File([res.data], fname, { type: "application/pdf" });
    const text = `Dear ${s.name},\n\nWelcome to Nisha Transport Corporation! Please find attached your joining letter for the post of ${s.designation || "Staff"} at our ${s.branch || "Chennai"} branch.\n\nKindly sign and return a copy as acceptance.\n\n— NTC HR`;
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: "NTC Joining Letter", text });
      return;
    }
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    a.click();
    URL.revokeObjectURL(url);
    const phone = String(s.phone || "").replace(/\D/g, "");
    const target = phone ? (phone.length === 10 ? `91${phone}` : phone) : "";
    window.open(`https://wa.me/${target}?text=${encodeURIComponent(text)}`, "_blank");
    toast.success("Joining letter PDF downloaded", { description: "Attach the downloaded PDF in the WhatsApp chat that just opened." });
  } catch (e) {
    if (e?.name === "AbortError") return;
    toast.error("Could not prepare the joining letter PDF");
  }
};

const latestSalaryFor = async (s) => {
  const r = await api.get("/salaries");
  const list = (Array.isArray(r.data) ? r.data : r.data?.rows || []).filter(
    (x) => (x.employee_name || "").trim().toLowerCase() === (s.name || "").trim().toLowerCase()
  );
  list.sort((a, b) => String(b.month || "").localeCompare(String(a.month || "")));
  return list[0] || null;
};

const printLatestSalarySlip = async (s) => {
  try {
    const rec = await latestSalaryFor(s);
    if (!rec) {
      toast.error(`No salary record for ${s.name} — add one in NTC Staff Salary first`);
      return;
    }
    printSalarySlip(rec);
  } catch {
    toast.error("Could not load salary records");
  }
};

const monthLabel = (m) => {
  if (!m) return "";
  const d = new Date(`${m}-01T00:00:00`);
  return isNaN(d) ? m : d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
};

const shareSalarySlipWhatsApp = async (s) => {
  try {
    const rec = await latestSalaryFor(s);
    if (!rec) {
      toast.error(`No salary record for ${s.name} — add one in NTC Staff Salary first`);
      return;
    }
    const rs = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN")}`;
    const lines = [
      "*NISHA TRANSPORT CORPORATION*",
      `*SALARY SLIP — ${monthLabel(rec.month)}*`,
      "",
      `Name: ${rec.employee_name}${rec.designation ? ` (${rec.designation})` : ""}`,
      `Branch: ${rec.office || "—"}`,
      `Basic: ${rs(rec.basic)}`,
      Number(rec.allowances) ? `Allowances: ${rs(rec.allowances)}` : "",
      Number(rec.deductions) || Number(rec.pf) || Number(rec.esi) || Number(rec.loan)
        ? `Deductions: ${rs((Number(rec.deductions) || 0) + (Number(rec.pf) || 0) + (Number(rec.esi) || 0) + (Number(rec.loan) || 0))}`
        : "",
      `*NET PAY: ${rs(rec.net_pay)}*`,
      `Mode: ${rec.payment_mode || "—"}${rec.status ? ` · ${rec.status}` : ""}`,
      "",
      "— NTC Accounts",
    ].filter((l) => l !== "");
    const digits = String(s.phone || "").replace(/\D/g, "");
    const target = digits ? `https://wa.me/${digits.length === 10 ? "91" + digits : digits}` : "https://wa.me/";
    window.open(`${target}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
  } catch {
    toast.error("Could not load salary records");
  }
};

const shareBranchPackWhatsApp = async (rows, token, branchLabel, lang, managerPhone = "") => {
  const pack = branchOpeningPackSections(rows, token, branchLabel, lang);
  if (!pack) return;
  try {
    toast.info(`Preparing Branch Pack PDF (${pack.sections.length} pages)…`);
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    const pw = 210, ph = 297, margin = 8;
    for (let i = 0; i < pack.sections.length; i++) {
      const el = document.createElement("div");
      el.style.cssText = "position:fixed;left:-20000px;top:0;background:#ffffff;width:780px;padding:10px;";
      el.innerHTML = pack.sections[i];
      document.body.appendChild(el);
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
      document.body.removeChild(el);
      let w = pw - margin * 2;
      let h = (canvas.height / canvas.width) * w;
      if (h > ph - margin * 2) {
        h = ph - margin * 2;
        w = (canvas.width / canvas.height) * h;
      }
      if (i > 0) pdf.addPage();
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.85), "JPEG", (pw - w) / 2, margin, w, h);
    }
    const fname = `branch-opening-pack-${pack.bl.replace(/\s+/g, "-").toLowerCase()}.pdf`;
    const blob = pdf.output("blob");
    const file = new File([blob], fname, { type: "application/pdf" });
    const text = `NTC Branch Opening Pack — ${pack.bl}\n${pack.list.length} staff · directory, ID cards & welcome kits (${lang}) attached.\n— Nisha Transport Corporation`;
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: "NTC Branch Opening Pack", text });
      return;
    }
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    a.click();
    URL.revokeObjectURL(url);
    const digits = String(managerPhone || "").replace(/\D/g, "");
    const target = digits ? (digits.length === 10 ? `91${digits}` : digits) : "";
    window.open(`https://wa.me/${target}?text=${encodeURIComponent(text)}`, "_blank");
    toast.success("Branch Pack PDF downloaded", { description: target ? "The branch manager's chat just opened — attach the downloaded PDF." : "Pick the branch manager's chat in WhatsApp and attach the downloaded PDF." });
  } catch (e) {
    if (e?.name === "AbortError") return;
    toast.error("Could not prepare the Branch Pack PDF");
  }
};

const shareJoinerKitWhatsApp = async (s, lang = "English") => {
  try {
    toast.info("Preparing joiner kit image…");
    const el = document.createElement("div");
    el.style.cssText = "position:fixed;left:-20000px;top:0;background:#ffffff;width:760px;padding:10px;";
    el.innerHTML = newJoinerKitHtml(s, lang);
    document.body.appendChild(el);
    const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
    document.body.removeChild(el);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
    const fname = `joiner-kit-${String(s.staff_code || s.name || "staff").replace(/\s+/g, "-").toLowerCase()}.png`;
    const file = new File([blob], fname, { type: "image/png" });
    const text = `Dear ${s.name}, welcome to Nisha Transport Corporation! Your New Joiner Kit is attached — it has your Staff Code (${s.staff_code || "—"}) and first sign-in steps.`;
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: "NTC New Joiner Kit", text });
      return;
    }
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    a.click();
    URL.revokeObjectURL(url);
    const phone = String(s.phone || "").replace(/\D/g, "");
    const target = phone ? (phone.length === 10 ? `91${phone}` : phone) : "";
    window.open(`https://wa.me/${target}?text=${encodeURIComponent(text)}`, "_blank");
    toast.success("Joiner kit image downloaded", { description: "Attach the downloaded image in the WhatsApp chat that just opened." });
  } catch (e) {
    if (e?.name === "AbortError") return;
    toast.error("Could not prepare the joiner kit image");
  }
};

const shareCardImageWhatsApp = async (s, kind, profile, token, theme) => {
  try {
    toast.info(kind === "id" ? "Preparing ID card image…" : "Preparing visiting card image…");
    const html = kind === "id" ? staffIdCardShareHtml(s, token) : await staffVisitingCardShareHtml(s, profile || {}, token, theme);
    const el = document.createElement("div");
    el.style.cssText = "position:fixed;left:-20000px;top:0;background:#ffffff;";
    el.innerHTML = html;
    document.body.appendChild(el);
    const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
    document.body.removeChild(el);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
    const fname = `${kind === "id" ? "id-card" : "visiting-card"}-${String(s.staff_code || s.name || "staff").replace(/\s+/g, "-").toLowerCase()}.png`;
    const file = new File([blob], fname, { type: "image/png" });
    const text = `Dear ${s.name}, please find your NTC ${kind === "id" ? "Staff ID Card" : "Visiting Card"} attached. — Nisha Transport Corporation`;
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: "NTC Card", text });
      return;
    }
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    a.click();
    URL.revokeObjectURL(url);
    const phone = String(s.phone || "").replace(/\D/g, "");
    const target = phone ? (phone.length === 10 ? `91${phone}` : phone) : "";
    window.open(`https://wa.me/${target}?text=${encodeURIComponent(text)}`, "_blank");
    toast.success("Card image downloaded", { description: "Attach the downloaded image in the WhatsApp chat that just opened." });
  } catch (e) {
    if (e?.name === "AbortError") return;
    toast.error("Could not prepare the card image");
  }
};

const shareCardOnWhatsApp = (s, p) => { // eslint-disable-line no-unused-vars
  const c = p || {};
  const lines = [
    `*${(c.name || "NISHA TRANSPORT CORPORATION").toUpperCase()}*`,
    "Fleet · Freight · Logistics · Surface · Air",
    "",
    `*${s.name || ""}*`,
    `${s.designation || "Staff"}${s.branch ? " · " + s.branch : ""}`,
    s.phone ? `📞 ${s.phone}` : "",
    s.email || c.email ? `✉️ ${s.email || c.email}` : "",
    c.website ? `🌐 ${c.website}` : "",
    s.branch_address || c.address ? `📍 ${s.branch_address || c.address}` : "",
    c.gstin ? `GSTIN: ${c.gstin}` : "",
  ].filter((l) => l !== "");
  window.open(`https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
};

export default function StaffMaster() {
  const token = localStorage.getItem("ntc_token");
  const [profile, setProfile] = useState(null);
  const [cardTheme, setCardThemeState] = useState(() => localStorage.getItem("ntc_vcard_theme") || "Light Grey");
  const setCardTheme = (t) => { localStorage.setItem("ntc_vcard_theme", t); setCardThemeState(t); };
  const [kitLang, setKitLangState] = useState(() => localStorage.getItem("ntc_kit_lang") || "English");
  const setKitLang = (l) => { localStorage.setItem("ntc_kit_lang", l); setKitLangState(l); };
  useEffect(() => {
    api.get("/company-profile").then((r) => setProfile(r.data)).catch(() => {});
  }, []);
  const deactRef = useRef(null);
  const alertStatusChange = async (rec, mode) => {
    let mob = "";
    try {
      const r = await api.get("/settings/otp-mobile");
      mob = String(r.data?.mobile || "").replace(/\D/g, "");
    } catch {}
    const target = mob ? (mob.length === 10 ? "91" + mob : mob) : "";
    const deact = mode === "deact";
    const msg = [
      `NTC Staff Master — STAFF CODE ${deact ? "DEACTIVATED" : "RE-ACTIVATED"}`,
      `Staff: ${rec.name || "—"}`,
      `Staff Code: ${rec.staff_code || "—"}`,
      `Branch: ${rec.branch || "Chennai"}`,
      `Date: ${new Date().toLocaleString("en-IN")}`,
      deact ? "Login with this Staff Code is now BLOCKED." : "Login with this Staff Code is now RESTORED.",
    ].join("\n");
    window.open(`https://wa.me/${target}?text=${encodeURIComponent(msg)}`, "_blank");
    toast.info(`WhatsApp ${deact ? "deactivation" : "re-activation"} alert ready — press Send`);
  };
  return (
    <EntityManager
      title="NTC Staff Master"
      subtitle="One saved staff list — Attendance and Salary pick names from here."
      endpoint="staff-master"
      entityName="Staff"
      confirmSave={(form, rows, editing) => {
        const prev = editing ? editing.status || "Active" : null;
        deactRef.current = editing && prev !== "Inactive" && form.status === "Inactive" ? "deact" : editing && prev === "Inactive" && form.status === "Active" ? "react" : null;
        return true;
      }}
      onSaved={(saved) => {
        if (deactRef.current) {
          const mode = deactRef.current;
          deactRef.current = null;
          alertStatusChange(saved || {}, mode);
        }
      }}
      headerExtra={(rows) => (
        <div className="flex flex-wrap items-center gap-2">
          <BulkIdCards rows={rows} token={token} profile={profile} cardTheme={cardTheme} setCardTheme={setCardTheme} kitLang={kitLang} setKitLang={setKitLang} />
          <Button variant="outline" disabled={!rows.length} onClick={() => printStaffDirectory(rows, token)} data-testid="print-staff-directory-btn">
            <Printer className="mr-2 h-4 w-4" /> Print Directory
          </Button>
          <Button variant="outline" disabled={!rows.length} onClick={() => printBranchDirectory(rows)} data-testid="print-branch-directory-btn">
            <Printer className="mr-2 h-4 w-4" /> Branch Directory
          </Button>
          <Button variant="outline" disabled={!rows.length} onClick={() => exportDirectoryExcel(rows)} data-testid="export-branch-directory-btn">
            <Download className="mr-2 h-4 w-4" /> Directory Excel
          </Button>
          <Button
            variant="outline"
            className="border-pink-300 text-pink-700 hover:bg-pink-50"
            disabled={!rows.length}
            onClick={() => { if (!printBirthdaysThisMonth(rows)) toast.info("No staff birthdays this month."); }}
            data-testid="print-birthdays-month-btn"
          >
            <Printer className="mr-2 h-4 w-4" /> Birthdays This Month
          </Button>
        </div>
      )}
      formExtra={(form, setForm) => (
        <>
          <PhotoUploader form={form} setForm={setForm} />
          <DocumentsUploader form={form} setForm={setForm} />
        </>
      )}
      formExtraAfter="name"
      defaults={{ name: "", staff_code: "", designation: "", branch: "Chennai", branch_address: "", phone: "", email: "", aadhar_no: "", pan_no: "", leave_quota: 0, emergency_contact: "", blood_group: "", license_expiry: "", card_valid_upto: "", joining_date: "", leaving_date: "", birthday: "", status: "Active", photo_path: "", photo_filename: "" }}
      fields={[
        { key: "name", label: "Staff Name", type: "text", placeholder: "Full name", required: true },
        { key: "staff_code", label: "Staff Code", type: "text", placeholder: "e.g. NTC-001" },
        { key: "designation", label: "Designation", type: "text", placeholder: "e.g. Branch Manager" },
        { key: "branch", label: "Branch", type: "select", options: BRANCHES },
        { key: "branch_address", label: "Branch Address", type: "textarea", placeholder: "Full branch address for visiting card (e.g. Tada / Jaipur branch address)" },
        { key: "phone", label: "Mobile No.", type: "text", placeholder: "10-digit mobile" },
        { key: "email", label: "Email ID", type: "text", placeholder: "name@example.com" },
        { key: "aadhar_no", label: "Aadhaar Card No.", type: "text", placeholder: "1234 5678 9012" },
        { key: "pan_no", label: "PAN Card No.", type: "text", placeholder: "ABCDE1234F" },
        { key: "emergency_contact", label: "Emergency Contact", type: "text", placeholder: "Name / phone for emergencies" },
        { key: "blood_group", label: "Blood Group", type: "select", options: ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"] },
        { key: "leave_quota", label: "Yearly Leave Quota (days)", type: "number", placeholder: "e.g. 24" },
        { key: "license_expiry", label: "License Expiry Date", type: "date" },
        { key: "card_valid_upto", label: "ID Card Valid Upto", type: "date" },
        { key: "joining_date", label: "Joining Date", type: "date" },
        { key: "leaving_date", label: "Leaving Date", type: "date" },
        { key: "birthday", label: "Birthday (DOB)", type: "date" },
        { key: "status", label: "Status", type: "select", options: ["Active", "Inactive"] },
      ]}
      columns={[
        { key: "sl", label: "Sl No.", render: (r, i) => i + 1 },
        {
          key: "photo",
          label: "Photo",
          render: (r) =>
            r.photo_path ? (
              <img src={`${API}/files/${r.photo_path}?auth=${token}`} alt={r.name} className="h-9 w-9 rounded-md border border-border object-cover" data-testid="staff-photo-thumb" />
            ) : (
              <span className="grid h-9 w-9 place-items-center rounded-md border border-dashed border-slate-300 text-slate-400"><User className="h-4 w-4" /></span>
            ),
        },
        { key: "staff_code", label: "Staff Code", render: (r) => r.staff_code || "—" },
        { key: "name", label: "Staff Name" },
        { key: "designation", label: "Designation", render: (r) => r.designation || "—" },
        { key: "branch", label: "Branch", render: (r) => r.branch || "Chennai" },
        { key: "branch_address", label: "Branch Address", render: (r) => (r.branch_address ? <span className="block max-w-[200px] truncate" title={r.branch_address}>{r.branch_address}</span> : "—") },
        {
          key: "phone",
          label: "Mobile No.",
          render: (r) =>
            r.phone ? (
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <a href={`tel:${r.phone}`} onClick={(e) => e.stopPropagation()} className="font-medium text-primary hover:underline" title="Call" data-testid="staff-phone-call-link">{r.phone}</a>
                <a
                  href={`https://wa.me/${String(r.phone).replace(/\D/g, "").length === 10 ? "91" + String(r.phone).replace(/\D/g, "") : String(r.phone).replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-emerald-600 hover:text-emerald-700"
                  title="WhatsApp"
                  data-testid="staff-phone-whatsapp-link"
                >
                  <MessageCircle className="h-3.5 w-3.5" />
                </a>
              </span>
            ) : (
              "—"
            ),
        },
        { key: "email", label: "Email ID", render: (r) => r.email || "—" },
        { key: "joining_date", label: "Joining Date", render: (r) => (r.joining_date ? fmtDate(r.joining_date) : "—") },
        { key: "leaving_date", label: "Leaving Date", render: (r) => (r.leaving_date ? fmtDate(r.leaving_date) : "—") },
        {
          key: "new_joining",
          label: "New Joining",
          render: (r) => {
            if (!r.joining_date) return "—";
            const d = Math.floor((Date.now() - new Date(r.joining_date).getTime()) / 86400000);
            return d >= 0 && d <= 90 ? (
              <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700" data-testid="new-joining-badge">NEW · {d}d</Badge>
            ) : (
              "—"
            );
          },
        },
        {
          key: "joining_letter",
          label: "Joining Letter",
          render: (r) =>
            r.documents?.joining_letter?.path ? (
              <a
                href={`${API}/files/${r.documents.joining_letter.path}?auth=${token}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs font-semibold text-emerald-600 hover:underline"
                onClick={(e) => e.stopPropagation()}
                data-testid="joining-letter-link"
              >
                View
              </a>
            ) : (
              "—"
            ),
        },
        { key: "birthday", label: "Birthday", render: (r) => (r.birthday ? fmtDate(r.birthday) : "—") },
        {
          key: "birthday_wish",
          label: "Birthday Wish",
          render: (r) => {
            const wa = (text) => {
              const d = String(r.phone || "").replace(/\D/g, "");
              return `https://wa.me/${d.length === 10 ? "91" + d : d}?text=${encodeURIComponent(text)}`;
            };
            return (
              <div className="flex items-center gap-1">
                {r.phone ? (
                  <>
                    <a
                      href={wa(`🎂 Happy Birthday, ${r.name}! Wishing you great health, happiness and success in the year ahead. Warm wishes from all of us at Nisha Transport Corporation.\n\n— Admin, Nisha Transport Corporation`)}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100"
                      title={`Send birthday wish to ${r.name} from Admin`}
                      data-testid="staff-bday-admin-btn"
                    >
                      <MessageCircle className="h-3 w-3" /> Admin
                    </a>
                    <a
                      href={wa(`🎂 Dear ${r.name}, Happy Birthday! Your dedication means a lot to our company. Wishing you great health, happiness and continued success in the year ahead.\n\n— Managing Director, Nisha Transport Corporation`)}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 rounded-full border border-blue-300 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700 hover:bg-blue-100"
                      title={`Send birthday wish to ${r.name} from Company MD`}
                      data-testid="staff-bday-md-btn"
                    >
                      <MessageCircle className="h-3 w-3" /> MD
                    </a>
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground">No phone</span>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); printStaffBirthdayCard(r); }}
                  className="inline-flex items-center gap-1 rounded-full border border-pink-300 bg-pink-50 px-2 py-0.5 text-[11px] font-semibold text-pink-700 hover:bg-pink-100"
                  title={`Open birthday card for ${r.name}`}
                  data-testid="staff-bday-card-btn"
                >
                  <Printer className="h-3 w-3" /> Card
                </button>
              </div>
            );
          },
        },
        {
          key: "idcard",
          label: "ID Card",
          render: (r) => (
            <div className="flex items-center">
              <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:text-blue-700" title="Print ID Card" onClick={(e) => { e.stopPropagation(); printStaffIdCard(r, token); }} data-testid="print-id-card-btn">
                <IdCard className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600 hover:text-emerald-700" title="Print Visiting Card" onClick={(e) => { e.stopPropagation(); printStaffVisitingCard(r, profile, token, cardTheme); }} data-testid="print-staff-vcard-btn">
                <Contact className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600 hover:text-amber-700" title="Print Appointment Letter" onClick={(e) => { e.stopPropagation(); printStaffJoiningLetter(r, localStorage.getItem("ntc_token")); }} data-testid="print-joining-letter-btn">
                <FileText className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-teal-600 hover:text-teal-700" title="Print New Joiner Kit (Staff Code + first-login steps)" onClick={(e) => { e.stopPropagation(); printNewJoinerKit(r, kitLang); }} data-testid="print-joiner-kit-btn">
                <UserPlus className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:text-green-700" title="WhatsApp New Joiner Kit (image)" onClick={(e) => { e.stopPropagation(); shareJoinerKitWhatsApp(r, kitLang); }} data-testid="share-joiner-kit-whatsapp-btn">
                <UserPlus className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600 hover:text-emerald-700" title="WhatsApp Joining Letter (PDF)" onClick={(e) => { e.stopPropagation(); shareJoiningLetterWhatsApp(r); }} data-testid="whatsapp-joining-letter-btn">
                <Send className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-sky-600 hover:text-sky-700" title="Print Experience / Relieving Letter" onClick={(e) => { e.stopPropagation(); printStaffExperienceLetter(r, r.leaving_date); }} data-testid="print-experience-letter-btn">
                <FileCheck className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-violet-600 hover:text-violet-700" title="Print Salary Slip (latest month)" onClick={(e) => { e.stopPropagation(); printLatestSalarySlip(r); }} data-testid="print-salary-slip-btn">
                <Banknote className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600 hover:text-emerald-700" title="WhatsApp Salary Slip to staff" onClick={(e) => { e.stopPropagation(); shareSalarySlipWhatsApp(r); }} data-testid="whatsapp-salary-slip-btn">
                <Wallet className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:text-green-700" title="WhatsApp ID Card (image)" onClick={(e) => { e.stopPropagation(); shareCardImageWhatsApp(r, "id", profile, token, cardTheme); }} data-testid="share-idcard-whatsapp-btn">
                <IdCard className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:text-green-700" title="WhatsApp Visiting Card (image)" onClick={(e) => { e.stopPropagation(); shareCardImageWhatsApp(r, "visiting", profile, token, cardTheme); }} data-testid="share-vcard-whatsapp-btn">
                <MessageCircle className="h-4 w-4" />
              </Button>
            </div>
          ),
        },
        {
          key: "status",
          label: "Status",
          render: (r) => (
            <Badge variant="outline" className={r.status === "Inactive" ? "border-slate-300 bg-slate-50 text-slate-500" : "border-emerald-300 bg-emerald-50 text-emerald-700"}>
              {r.status || "Active"}
            </Badge>
          ),
        },
      ]}
      filters={[
        {
          key: "q",
          label: "Search",
          type: "text",
          placeholder: "Name / code / designation / mobile",
          match: (r, v) => {
            const q = String(v || "").trim().toLowerCase();
            if (!q) return true;
            return [r.name, r.staff_code, r.designation, r.branch, r.phone, r.email, r.branch_address]
              .some((x) => String(x || "").toLowerCase().includes(q));
          },
        },
        { key: "branch", label: "Branch", type: "select", options: ["All", ...BRANCHES], match: (r, v) => !v || v === "All" || (r.branch || "Chennai") === v },
        { key: "status", label: "Status", type: "select", options: ["All", "Hide Inactive", "Inactive Only"], match: (r, v) => !v || v === "All" || (v === "Hide Inactive" ? (r.status || "Active") !== "Inactive" : (r.status || "Active") === "Inactive") },
        {
          key: "bday_month",
          label: "Birthday Month",
          type: "select",
          options: ["All", "This Month", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
          match: (r, v) => {
            if (!v || v === "All") return true;
            if (!r.birthday) return false;
            const m = Number(String(r.birthday).slice(5, 7));
            if (v === "This Month") return m === new Date().getMonth() + 1;
            return m === ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].indexOf(v) + 1;
          },
        },
      ]}
    />
  );
}
